// Global CSS for the public marketing pages. Shared by the client entrypoint (hebe.jsx) and the SSR
// entrypoint (ssr.jsx): both must apply the SAME rules or the server markup and the markup the client
// hydrates disagree, and styled-components mints a second sheet instead of rehydrating the first.
//
// IMPORTANT — why this is a plain string in a one-element array rather than the css`` helper:
// both entrypoints call `createGlobalStyle(GlobalCSS)` as an ordinary function, not as a tagged
// template. That only works when the value looks like a template-strings array. The css`` helper
// returns ONE element when the template has no interpolations, which is why the original version
// worked, but returns an interleaved array of strings and values as soon as it does — and those get
// joined wrongly, silently dropping every rule. Resolving the tokens here and handing over a single
// finished string keeps the call sites untouched and cannot regress that way again.
//
// @font-face lives in public/fonts/fonts.css, not here: stylis mis-parses a multi-line
// unicode-range and swallows the rules that follow it.
import { color, font, leading } from "./tokens";

const SHEET = `
  [data-error] {
    /* overcome specificity for onboarding-form-helpers */
    && {
      border: 1px solid #da3333;
    }
  }

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  html {
    -webkit-text-size-adjust: 100%;
    /* The masthead is fixed and two rows tall, so an anchor jump would otherwise land with the
       section heading hidden underneath it. Applies to #hash navigation and to scrollIntoView. */
    scroll-padding-top: 110px;
    /* In-page jumps glide rather than teleport. Neutralised by the prefers-reduced-motion block
       below, which sets scroll-behavior back to auto. */
    scroll-behavior: smooth;
  }

  html,
  body {
    max-width: 100vw;
    /* clip (not hidden) so horizontal overflow is contained WITHOUT creating a scroll
       container — hidden breaks position: sticky on iOS Safari. */
    overflow-x: clip;
  }

  body {
    margin: 0;
    padding: 0;
    font-family: ${font.body};
    font-size: 1rem;
    line-height: ${leading.normal};
    color: ${color.ink};
    background: ${color.canvas};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  a {
    color: inherit;
    text-decoration: none;
  }

  /* An image that never exceeds its column. Several marketing sections used to rely on the parent
     clipping instead, which broke the moment a cell changed aspect. */
  img,
  video {
    max-width: 100%;
    height: auto;
    display: block;
  }

  /* A visible focus ring everywhere, because the UA default is invisible on both the ink and the
     bone grounds. /hebe previously styled focus on exactly one element. */
  :focus-visible {
    outline: 2px solid ${color.focus};
    outline-offset: 3px;
    border-radius: 2px;
  }

  /* Motion is decorative on every marketing surface, so honour the preference globally rather than
     per component. Previously only pages/hebe/identity did this, and /hebe ran a 25s Ken Burns, a
     5s carousel and four staggered entrance animations regardless. */
  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }
`;

export const GlobalCSS = [SHEET];
